import * as angular from 'angular';
import { States } from "../../shared/config/constant.module";
import template from './productionKPIs.tpl.html';
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import * as _ from "lodash";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import KpiServiceName, { KpiService } from '../../services/dashboard/kpi.service';
import * as moment from 'moment';
import { StateParams } from '../../common';

let requires = [LoaderServiceName, KpiServiceName];

function productionKPIsConfig($stateProvider: angular.ui.IStateProvider) {
  $stateProvider.state({
    name: States.KPIS,
    url: '/production-kpis',
    controller: 'ProductionKPIsCtrl as vm',
    template: template,
    data: { pageTitle: 'Production KPIs' }
  });
}

export interface ProductionKPIsParams extends StateParams {
  reportDate: string;
}

class ProductionKPIsCtrl {

  asOfDate: string;
  reportDate: Date;
  form: angular.IFormController;
  showReport: boolean;
  dailyKPIs: apsViewModels.ProductionKPI[];
  monthlyKPIs: apsViewModels.ProductionKPI[];
  maxLegacyDate: string;
  maxSafetyDate: string;
  maxQualityDate: string;
  readonly allPlants: apsViewModels.ProductionKPIPlant[] = [
    { id: 7, name: '07-JAX' },
    { id: 12, name: '12-SHA' },
    { id: 14, name: '14-WR' },
    { id: 15, name: '15-HEN' },
    { id: 41, name: '41-ELM' },
    { id: 42, name: '42-LAW' }
  ];

  plantList: apsViewModels.ProductionKPIPlant[] = [...this.allPlants];

  private $state: angular.ui.IStateService;
  private $stateParams: ProductionKPIsParams;
  loaderService: LoaderService;
  kpiService: KpiService;

  lightgray = 'lightgray';
  red = 'red';
  green = 'green';
  yellow = 'yellow';

  constructor(
    loaderService: LoaderService,
    $state: angular.ui.IStateService,
    $stateParams: ProductionKPIsParams,
    kpiService: KpiService) {

    this.$state = $state;
    this.$stateParams = $stateParams;
    this.loaderService = loaderService;
    this.kpiService = kpiService;

    this.loadState($stateParams);

    let maxDatePromise = this.kpiService.getLastUpdatedDates();
    maxDatePromise.then((data: apsViewModels.ProductionKPIDates) => {
      this.reportDate = moment().add(-1, 'days').toDate();
      this.asOfDate = data.legacyDataLastUpdatedDate;
      this.maxLegacyDate = this.asOfDate;
      this.maxSafetyDate = data.safetyDataLastUpdatedDate == "" ? "unknown" : data.safetyDataLastUpdatedDate;
      this.maxQualityDate = data.qualityDataLastUpdatedDate == "" ? "unknown" : data.qualityDataLastUpdatedDate;
    })
  }

  generateReport() {
    if (this.form.$invalid) {
      return;
    }

    // Reset to original list
    this.plantList = [...this.allPlants];

    // Remove JAX if report date is greater than 19th November 2024

    if (this.reportDate >  new Date('2024-11-20')) {
      this.plantList = this.plantList.filter(plant => plant.id !== 7);
    }

    this.showReport = false;
    this.saveState();
    this.asOfDate = moment(this.reportDate).format("dddd, MM/DD/YYYY");
    this.loaderService.loadingPromise(this.runReport());
  }

  private runReport(): angular.IPromise<any> {
    let monthlyPromise = this.kpiService.getMonthlyProductionKPIs(this.asOfDate);

    monthlyPromise.then((data: apsViewModels.ProductionKPI[]) => {
      this.monthlyKPIs = data;
      this.setCellColors(this.monthlyKPIs, false);

      let dailyPromise = this.kpiService.getDailyProductionKPIs(this.asOfDate);
      dailyPromise.then((data: apsViewModels.ProductionKPI[]) => {
        this.dailyKPIs = data;
        this.setCellColors(this.dailyKPIs, true);
        this.showReport = true;
      })
    });

    return monthlyPromise;
  }

  setCellColors(plantKPIs: apsViewModels.ProductionKPI[], isDay: boolean) {
    plantKPIs.forEach(plantKPI => {
      plantKPI.safetyColor = this.setSafetyColor(plantKPI.safetyCount);
      plantKPI.qualityColor = this.setQualityColor(plantKPI.qualityCount);
      plantKPI.tonsColor = this.setTonsPackedColor(plantKPI.tonsPacked, this.monthlyKPIs.filter(x => x.plant == plantKPI.plant)[0].aopTonsPacked, isDay ? 1 : this.reportDate.getDate());
      plantKPI.packToMeltColor = this.setPTMColor(plantKPI.packToMelt, plantKPI.aopPackToMelt);
      plantKPI.furnaceUtilizationColor = this.setFurnaceUtilizationColor(plantKPI.furnaceUtilization);
    });
  }

  setSafetyColor(count: number): string {
    if (count == null || count == 999) {
      return this.lightgray;
    }

    if (count == 0) {
      return this.green;
    }

    return this.red;
  }

  setQualityColor(count: number): string {
    if (count == null || count == 999) {
      return this.lightgray;
    }

    if (count == 0) {
      return this.green;
    }

    return this.red;
  }

  setTonsPackedColor(tons: number, aop: number, days: number): string {
    let proratedAOP = (aop / this.reportDate.getDate()) * days;

    if (tons > proratedAOP) {
      return this.green;
    }

    if (tons == proratedAOP) {
      return this.yellow;
    }

    return this.red;
  }

  setPTMColor(ptm: number, aopPTM: number): string {
    if (ptm > aopPTM) {
      return this.green;
    }

    if (aopPTM - ptm <= 1) {
      return this.yellow;
    }

    return this.red;
  }

  setFurnaceUtilizationColor(utilization: number): string {
    if (utilization > 95.0) {
      return this.green;
    }

    if (utilization >= 80.0) {
      return this.yellow;
    }

    return this.red;
  }

  loadState(params: ProductionKPIsParams): void {
    if (params.reportDate) {
      var reportDate = moment(params.reportDate);
      if (reportDate.isValid()) {
        this.reportDate = reportDate.toDate();
      }
    }
  }

  saveState(): void {

    var params: ProductionKPIsParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportDate: this.reportDate ? this.reportDate.toISOString() : undefined
    };

    this.$state.transitionTo(this.$state.current.name, params);
  }

  getKPI(plant: string, item: string, isDay: boolean) {
    let plantKPIs = isDay ? this.dailyKPIs : this.monthlyKPIs;

    if (plantKPIs != null) {
      switch (item) {
        case 'safety':
          let safetyCount = plantKPIs.filter(x => x.plant == plant)[0].safetyCount;
          return safetyCount == null ? "(not available)" : plantKPIs.filter(x => x.plant == plant)[0].safetyCount.toFixed(0);
        case 'safetyColor':
          return plantKPIs.filter(x => x.plant == plant)[0].safetyColor;
        case 'quality':
          let qualityCount = plantKPIs.filter(x => x.plant == plant)[0].qualityCount;
          return qualityCount == null ? "(not available)" : plantKPIs.filter(x => x.plant == plant)[0].qualityCount.toFixed(0);
        case 'qualityColor':
          return plantKPIs.filter(x => x.plant == plant)[0].qualityColor;
        case 'tons':
          return plantKPIs.filter(x => x.plant == plant)[0].tonsPacked.toFixed(1);
        case 'tonsColor':
          return plantKPIs.filter(x => x.plant == plant)[0].tonsColor;
        case 'aop':
          return this.monthlyKPIs.filter(x => x.plant == plant)[0].aopTonsPacked.toFixed(1);
        case 'ptm':
          return plantKPIs.filter(x => x.plant == plant)[0].packToMelt.toFixed(1);
        case 'ptmColor':
          return plantKPIs.filter(x => x.plant == plant)[0].packToMeltColor;
        case 'utilization':
          return plantKPIs.filter(x => x.plant == plant)[0].furnaceUtilization.toFixed(1);
        case 'utilizationColor':
          return plantKPIs.filter(x => x.plant == plant)[0].furnaceUtilizationColor;
        case 'legacy':
          return plantKPIs.filter(x => x.plant == plant)[0].isLegacy == true ? "*" : '';
        default:
          return null;
      }
    }
  }
}

export default angular.module('aps.controller.productionkpis', requires)
  .config(productionKPIsConfig)
  .controller('ProductionKPIsCtrl', ProductionKPIsCtrl)
  .name;



// WEBPACK FOOTER //
// ./src/app/controllers/dashboard/productionKPIs.controller.ts